import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Checkbox } from '../components/ui/checkbox';
import { Slider } from '../components/ui/slider';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { ToggleGroup, ToggleGroupItem } from '../components/ui/toggle-group';
import { Grid, List, Search } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const CONDITIONS = ['New', 'Excellent', 'VeryGood', 'Good'];

export default function Catalog() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [watches, setWatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('grid');
  const [totalPages, setTotalPages] = useState(1);
  const [dynamicBrands, setDynamicBrands] = useState([]);
  const [dynamicMaterials, setDynamicMaterials] = useState([]);
  const [showAllBrands, setShowAllBrands] = useState(false);

  // Brands tab: ?brand= (empty) means "browse brands" mode
  const isBrandsMode = searchParams.has('brand');
  const brandParam = searchParams.get('brand') || '';

  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [selectedBrands, setSelectedBrands] = useState(brandParam ? [brandParam] : []);
  const [selectedConditions, setSelectedConditions] = useState([]);
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 500000]);
  const [sortBy, setSortBy] = useState('newest');
  const [page, setPage] = useState(1);

  useEffect(() => { fetchCategories(); }, []);

  useEffect(() => {
    fetchWatches();
  }, [selectedBrands, selectedConditions, selectedMaterials, priceRange, sortBy, page, search]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/categories`);
      setDynamicBrands(res.data.brands || []);
      setDynamicMaterials(res.data.materials || []);
    } catch (e) { console.error('Error fetching categories:', e); }
  };

  const fetchWatches = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (selectedBrands.length > 0 && selectedBrands[0]) params.append('brand', selectedBrands[0]);
      if (selectedConditions.length > 0) params.append('condition', selectedConditions[0]);
      if (selectedMaterials.length > 0) params.append('material', selectedMaterials[0]);
      if (priceRange[0] > 0) params.append('min_price', String(priceRange[0]));
      if (priceRange[1] < 500000) params.append('max_price', String(priceRange[1]));
      params.append('sort_by', sortBy);
      params.append('page', String(page));
      const res = await axios.get(`${API_URL}/api/listings?${params.toString()}`);
      setWatches(res.data.listings);
      setTotalPages(res.data.pages);
    } catch (e) { console.error('Error fetching watches:', e); }
    finally { setLoading(false); }
  };

  const selectBrand = (brand) => {
    setSelectedBrands([brand]);
    setPage(1);
    navigate(`/catalog?brand=${encodeURIComponent(brand)}`, { replace: true });
  };

  const handleBrandToggle = (brand) => {
    setSelectedBrands(prev => prev.includes(brand) ? [] : [brand]);
    setPage(1);
  };
  const handleConditionToggle = (condition) => {
    setSelectedConditions(prev => prev.includes(condition) ? [] : [condition]);
    setPage(1);
  };
  const handleMaterialToggle = (material) => {
    setSelectedMaterials(prev => prev.includes(material) ? [] : [material]);
    setPage(1);
  };
  const handleSearch = () => { setPage(1); fetchWatches(); };
  const clearFilters = () => { setSelectedBrands([]); setSelectedConditions([]); setSelectedMaterials([]); setPriceRange([0, 500000]); setSearch(''); setPage(1); };

  const formatPrice = (price, currency = 'USD') => new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 0 }).format(price);

  const visibleBrands = showAllBrands ? dynamicBrands : dynamicBrands.slice(0, 4);

  return (
    <div className="min-h-screen py-8" data-testid="catalog-page">
      <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-[1280px]">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-display text-3xl sm:text-4xl text-foreground tracking-tight font-semibold mb-2">
            {isBrandsMode && !brandParam ? 'Browse by Brand' : selectedBrands[0] ? `${selectedBrands[0]} Watches` : 'Luxury Watch Catalog'}
          </h1>
          <p className="text-muted-foreground">
            {isBrandsMode && !brandParam ? 'Select a brand to explore their collection' : 'Browse our curated selection of verified timepieces'}
          </p>
        </div>

        {/* Brand Grid (shown when ?brand= with no value) */}
        {isBrandsMode && !brandParam && !selectedBrands[0] && (
          <div className="mb-10" data-testid="brands-grid">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {dynamicBrands.map(brand => (
                <button
                  key={brand}
                  onClick={() => selectBrand(brand)}
                  className="p-6 rounded-xl border border-border bg-card hover:border-primary/50 hover:bg-muted/30 transition-all text-center group"
                  data-testid={`brand-grid-${brand.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <span className="text-sm font-medium text-muted-foreground group-hover:text-foreground transition-colors">{brand}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="catalog-toolbar">
          <div className="flex-1 min-w-[200px] max-w-md">
            <div className="relative">
              <Input placeholder="Search brand, model, reference..." value={search} onChange={(e) => setSearch(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleSearch()} data-testid="catalog-search-input" className="pr-10" />
              <button onClick={handleSearch} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-muted rounded"><Search className="h-4 w-4 text-muted-foreground" /></button>
            </div>
          </div>
          <Select value={sortBy} onValueChange={setSortBy} data-testid="catalog-sort-select">
            <SelectTrigger className="w-[160px]"><SelectValue placeholder="Sort by" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="price-asc">Price: Low to High</SelectItem>
              <SelectItem value="price-desc">Price: High to Low</SelectItem>
            </SelectContent>
          </Select>
          <ToggleGroup type="single" value={view} onValueChange={(v) => v && setView(v)} data-testid="catalog-view-toggle">
            <ToggleGroupItem value="grid" aria-label="Grid view"><Grid className="h-4 w-4" /></ToggleGroupItem>
            <ToggleGroupItem value="list" aria-label="List view"><List className="h-4 w-4" /></ToggleGroupItem>
          </ToggleGroup>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-[260px,1fr] gap-8">
          {/* Filters Sidebar */}
          <aside className="lg:sticky lg:top-20 h-fit" data-testid="catalog-filters-sidebar">
            <Card className="p-4 border-border bg-card">
              <h3 className="font-medium text-foreground mb-4 text-sm">Filters</h3>
              <Accordion type="multiple" defaultValue={['brand', 'condition', 'price']} className="space-y-2">
                <AccordionItem value="brand">
                  <AccordionTrigger className="text-sm py-2">Brand</AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-2 pt-1">
                      {visibleBrands.map(brand => (
                        <div key={brand} className="flex items-center space-x-2">
                          <Checkbox id={`brand-${brand}`} checked={selectedBrands.includes(brand)} onCheckedChange={() => handleBrandToggle(brand)} data-testid={`filter-brand-${brand.toLowerCase().replace(/\s+/g, '-')}`} />
                          <label htmlFor={`brand-${brand}`} className="text-sm cursor-pointer flex-1">{brand}</label>
                        </div>
                      ))}
                      {dynamicBrands.length > 4 && (
                        <button onClick={() => setShowAllBrands(!showAllBrands)} className="text-xs text-primary hover:underline mt-1">{showAllBrands ? 'Show less' : 'View more brands'}</button>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="condition">
                  <AccordionTrigger className="text-sm py-2">Condition</AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-2 pt-1">
                      {CONDITIONS.map(condition => (
                        <div key={condition} className="flex items-center space-x-2">
                          <Checkbox id={`condition-${condition}`} checked={selectedConditions.includes(condition)} onCheckedChange={() => handleConditionToggle(condition)} data-testid={`filter-condition-${condition.toLowerCase().replace(/\s+/g, '-')}`} />
                          <label htmlFor={`condition-${condition}`} className="text-sm cursor-pointer flex-1">{condition}</label>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="material">
                  <AccordionTrigger className="text-sm py-2">Material</AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-2 pt-1">
                      {dynamicMaterials.map(material => (
                        <div key={material} className="flex items-center space-x-2">
                          <Checkbox id={`material-${material}`} checked={selectedMaterials.includes(material)} onCheckedChange={() => handleMaterialToggle(material)} data-testid={`filter-material-${material.toLowerCase().replace(/\s+/g, '-')}`} />
                          <label htmlFor={`material-${material}`} className="text-sm cursor-pointer flex-1">{material}</label>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="price">
                  <AccordionTrigger className="text-sm py-2">Price Range</AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 pt-2">
                      <Slider min={0} max={500000} step={5000} value={priceRange} onValueChange={setPriceRange} data-testid="filter-price-slider" />
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>${priceRange[0].toLocaleString()}</span>
                        <span>${priceRange[1].toLocaleString()}</span>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
              <Button variant="outline" size="sm" className="w-full mt-4 text-xs" onClick={clearFilters} data-testid="filter-clear-button">Clear Filters</Button>
            </Card>
          </aside>

          {/* Products */}
          <div>
            {loading ? (
              <div className={view === 'grid' ? 'grid grid-cols-2 md:grid-cols-3 gap-6' : 'space-y-4'}>
                {[...Array(9)].map((_, i) => (
                  <div key={i} className="animate-pulse"><Skeleton className="aspect-[4/5] w-full rounded-t-xl" /><div className="p-4 space-y-3"><Skeleton className="h-4 w-full" /><Skeleton className="h-3 w-3/4" /><Skeleton className="h-4 w-1/2" /></div></div>
                ))}
              </div>
            ) : watches.length === 0 ? (
              <div className="text-center py-16" data-testid="catalog-empty-state">
                <p className="text-muted-foreground mb-4">No watches found matching your criteria</p>
                <Button variant="outline" size="sm" onClick={clearFilters}>Clear Filters</Button>
              </div>
            ) : (
              <>
                <div className={view === 'grid' ? 'grid grid-cols-2 md:grid-cols-3 gap-6' : 'space-y-4'} data-testid="catalog-watches-container">
                  {watches.map((watch) => (
                    view === 'grid' ? (
                      <Card key={watch.id} className="group relative rounded-xl border border-border bg-card hover:shadow-[0_8px_40px_rgba(0,0,0,0.45)] transition-shadow cursor-pointer" onClick={() => navigate(`/watch/${watch.id}`)} data-testid="product-card">
                        <div className="aspect-[4/5] overflow-hidden rounded-t-xl bg-black/40 relative">
                          <img src={watch.images?.[0] || 'https://images.unsplash.com/photo-1589688934051-ca9059383f4f?w=800'} alt={watch.title} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
                          {watch.status === 'sold' && (
                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center"><Badge className="bg-red-600 text-white border-0 text-sm px-4 py-1">Sold</Badge></div>
                          )}
                        </div>
                        <div className="p-4">
                          <h3 className="text-sm font-medium text-foreground truncate" data-testid="product-title">{watch.title}</h3>
                          <p className="text-xs text-muted-foreground truncate mt-0.5">{watch.refNumber}</p>
                          <div className="mt-3 flex items-center justify-between">
                            <span className="text-primary font-semibold text-sm" data-testid="product-price-text">{formatPrice(watch.price, watch.currency)}</span>
                            <Badge variant="outline" className="text-[10px] bg-transparent border-primary/30 text-foreground">{watch.condition}</Badge>
                          </div>
                        </div>
                      </Card>
                    ) : (
                      <Card key={watch.id} className="group flex gap-4 p-4 rounded-xl border border-border bg-card hover:shadow-[0_8px_40px_rgba(0,0,0,0.45)] transition-shadow cursor-pointer" onClick={() => navigate(`/watch/${watch.id}`)} data-testid="product-card-list">
                        <div className="w-28 h-28 overflow-hidden rounded-lg bg-black/40 flex-shrink-0 relative">
                          <img src={watch.images?.[0] || 'https://images.unsplash.com/photo-1589688934051-ca9059383f4f?w=800'} alt={watch.title} className="h-full w-full object-cover" />
                          {watch.status === 'sold' && <div className="absolute inset-0 bg-black/40 flex items-center justify-center"><Badge className="bg-red-600 text-white border-0 text-xs">Sold</Badge></div>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base font-medium text-foreground mb-1">{watch.title}</h3>
                          <p className="text-sm text-muted-foreground mb-2">Ref: {watch.refNumber} &bull; {watch.year}</p>
                          <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{watch.description}</p>
                          <div className="flex items-center justify-between">
                            <span className="text-primary font-semibold">{formatPrice(watch.price, watch.currency)}</span>
                            <Badge variant="outline" className="text-xs bg-transparent border-primary/30 text-foreground">{watch.condition}</Badge>
                          </div>
                        </div>
                      </Card>
                    )
                  ))}
                </div>
                {totalPages > 1 && (
                  <div className="mt-8 flex items-center justify-center gap-2" data-testid="catalog-pagination">
                    <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} data-testid="pagination-prev-button">Previous</Button>
                    <span className="text-sm text-muted-foreground px-4">Page {page} of {totalPages}</span>
                    <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} data-testid="pagination-next-button">Next</Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
